import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { chatService, ConnectionStatus } from '../services/chat';
import { ChatMessage, ChatResponse } from '../types/chat';
import { useAccount } from './WalletProvider';
import { MangaBubble } from './chat/MangaBubble';
import {
  ChatContainer,
  MessagesContainer,
  InputContainer,
  Input,
} from './chat/ChatContainer';
import styled from 'styled-components';

const ConnectionStatusBar = styled.div<{ status: ConnectionStatus }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 8px;
  text-align: center;
  font-size: 14px;
  background: ${({ status }) => 
    status === 'connected' ? 'rgba(0, 255, 0, 0.1)' :
    status === 'connecting' ? 'rgba(255, 255, 0, 0.1)' :
    status === 'reconnecting' ? 'rgba(255, 165, 0, 0.1)' :
    'rgba(255, 0, 0, 0.1)'
  };
  color: ${({ status }) => 
    status === 'connected' ? '#00aa00' :
    status === 'connecting' ? '#aaaa00' :
    status === 'reconnecting' ? '#ff8c00' :
    '#aa0000'
  };
  transition: all 0.3s ease;
  opacity: ${({ status }) => status === 'connected' ? 0 : 1};
  transform: translateY(${({ status }) => status === 'connected' ? '-100%' : '0'});
`;

const ReconnectButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin: 0;
  font: inherit;

  &:hover {
    opacity: 0.8;
  }
`;

export function Chat() {
  const { account } = useAccount();
  const location = useLocation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isWaiting, setIsWaiting] = useState(true);
  const [canInput, setCanInput] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(chatService.getStatus());
  const chatServiceRef = useRef(chatService);
  const mountedRef = useRef(false);
  const previousAccountRef = useRef<string | null>(null);
  const [newMessageId, setNewMessageId] = useState<number | null>(null);

  const cleanupChat = () => {
    console.log('[Chat] Cleaning up chat state');
    setMessages([]);
    setInput('');
    setIsWaiting(true);
    setCanInput(false);
    if (mountedRef.current) {
      chatServiceRef.current.disconnect();
    }
  };

  const handleReconnect = () => {
    if (connectionStatus === 'disconnected') {
      console.log('[Chat] Manually reconnecting...');
      chatServiceRef.current.connect();
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const currentAccountId = account?.wallet_address || null;
    console.log('[Chat] Account changed:', { previous: previousAccountRef.current, current: currentAccountId });
    
    if (previousAccountRef.current !== currentAccountId) {
      cleanupChat();
      previousAccountRef.current = currentAccountId;
    }

    if (!account?.wallet_address || !mountedRef.current) {
      return;
    }

    console.log('[Chat] Setting up WebSocket handlers');
    
    chatServiceRef.current.setApiKey(account.api_key);
    
    chatServiceRef.current.onMessage((response: ChatResponse) => {
      if (!mountedRef.current) return;
      
      console.log('[Chat] Received response:', response);
      
      if (response.messages && response.messages.length > 0) {
        setMessages(prev => {
          const newMessages = [...prev, ...(response.messages || [])];
          console.log('[Chat] Updated messages:', newMessages);
          setNewMessageId(newMessages.length - 1);
          return newMessages;
        });
      }
      
      if (response.is_final) {
        console.log('[Chat] Received final response, enabling input');
        setIsWaiting(false);
        setCanInput(true);
        setTimeout(() => setNewMessageId(null), 1000);
      }
    });

    chatServiceRef.current.onError((error: Error) => {
      if (!mountedRef.current) return;
      
      console.error('[Chat] WebSocket error:', error);
      
      // Only show errors that are not related to connection/authentication
      if (!error.message.includes('WebSocket connection error') && 
          !error.message.includes('Failed to create WebSocket connection') &&
          !error.message.includes('API key not set') &&
          !error.message.includes('Maximum reconnection attempts reached')) {
        setMessages(prev => [...prev, {
          role: 'system',
          content: `Error: ${error.message}`
        }]);
      }
      
      setIsWaiting(false);
      setCanInput(true);
    });

    chatServiceRef.current.onStatusChange((status: ConnectionStatus) => {
      if (!mountedRef.current) return;
      console.log('[Chat] Connection status changed:', status);
      setConnectionStatus(status);
      
      if (status === 'connected') {
        setCanInput(true);
        setIsWaiting(false);
      } else {
        setCanInput(false);
      }
    });

    console.log('[Chat] Connecting WebSocket');
    chatServiceRef.current.connect();

    return () => {
      if (mountedRef.current) {
        console.log('[Chat] Component unmounting, cleaning up');
        chatServiceRef.current.disconnect();
      }
    };
  }, [account?.wallet_address]);

  // Add route change detection
  useEffect(() => {
    return () => {
      console.log('[Chat] Route changed, cleaning up immediately');
      cleanupChat();
    };
  }, [location]);

  const handleSend = () => {
    if (!input.trim() || isWaiting || !mountedRef.current || !canInput) {
      console.log('[Chat] Send blocked:', { 
        emptyInput: !input.trim(), 
        isWaiting,
        canInput 
      });
      return;
    }

    const userMessage: ChatMessage = {
      role: 'user',
      content: input.trim()
    };

    console.log('[Chat] Sending message:', userMessage);

    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      setNewMessageId(newMessages.length - 1);
      return newMessages;
    });
    setInput('');
    setIsWaiting(true);
    setCanInput(false);

    try {
      chatServiceRef.current.send(userMessage.content);
    } catch (error) {
      console.error('[Chat] Failed to send message:', error);
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Failed to send message. Please try again.'
      }]);
      setIsWaiting(false);
      setCanInput(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!account) {
    console.log('[Chat] No account, not rendering');
    return null;
  }

  const getConnectionMessage = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting to chat...';
      case 'reconnecting':
        return 'Reconnecting to chat...';
      case 'disconnected':
        return (
          <>
            Disconnected. <ReconnectButton onClick={handleReconnect}>Click to reconnect</ReconnectButton>
          </>
        );
    }
  };

  return (
    <ChatContainer>
      <ConnectionStatusBar status={connectionStatus}>
        {getConnectionMessage()}
      </ConnectionStatusBar>
      <MessagesContainer>
        {[...messages].reverse().map((msg, i) => (
          <MangaBubble 
            key={messages.length - 1 - i} 
            role={msg.role}
            isNew={messages.length - 1 - i === newMessageId}
          >
            {msg.content}
          </MangaBubble>
        ))}
      </MessagesContainer>
      <InputContainer>
        <Input
          value={input}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={canInput ? "Chat with Lily! ✨" : "Lily is typing..."}
          disabled={!canInput || isWaiting}
        />
      </InputContainer>
    </ChatContainer>
  );
} 